import React, { useState, useEffect } from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowRight, FileCheck2, Microscope, X, ZoomIn, ImageOff, Loader2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { api, resolveImg } from '../lib/api';

const Certificates = () => {
  const [viewer, setViewer] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/certificates').then(({ data }) => setCertifications(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Seo title="Certifications & Compliance" description="Kassam Trading Company is REAP-registered and a KCCI member with ISO 9001, ISO 22000, HACCP and Halal certifications. SGS and Bureau Veritas inspection available on request." path="/certificates" />
      <PageHero
        title="Certifications & Quality Assurance"
        subtitle="Every shipment is backed by internationally recognised certifications, with independent inspection available on request. Click any certificate to view it."
        crumb="Certificates"
      />

      <section className="max-w-7xl mx-auto px-6 py-20">
        {loading ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-ktc-green" size={34} /></div>
        ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((c, i) => (
            <Reveal key={c.abbr} delay={(i % 3) * 70}>
              <button
                onClick={() => setViewer(c)}
                className="group w-full text-left bg-ktc-ivoryLight rounded-xl border border-ktc-cream p-8 card-lift h-full relative"
              >
                <span className="absolute top-5 right-5 h-9 w-9 grid place-items-center rounded-full bg-ktc-cream text-ktc-green opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn size={17} />
                </span>
                <div className="h-16 w-16 grid place-items-center rounded-xl bg-ktc-green text-ktc-goldLight mb-6">
                  <ShieldCheck size={30} />
                </div>
                <div className="font-serif text-3xl font-bold text-ktc-gold">{c.abbr}</div>
                <h3 className="font-serif text-2xl font-semibold text-ktc-green mt-1">{c.name}</h3>
                <p className="text-sm text-ktc-charcoal/70 mt-3 leading-relaxed">{c.detail}</p>
                <span className="inline-flex items-center gap-1.5 text-ktc-green font-semibold text-sm mt-5 group-hover:gap-2.5 transition-all">
                  View certificate <ArrowRight size={15} />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
        )}
      </section>

      <section className="bg-ktc-cream/60">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-8">
          <Reveal className="bg-ktc-ivoryLight rounded-xl border border-ktc-cream p-8">
            <FileCheck2 className="text-ktc-gold" size={30} />
            <h3 className="font-serif text-2xl font-semibold text-ktc-green mt-4">Documented Compliance</h3>
            <p className="text-sm text-ktc-charcoal/70 mt-3 leading-relaxed">As a REAP-registered exporter (#2-1-99-1195), we provide complete export documentation, phytosanitary and quality certificates with every consignment.</p>
          </Reveal>
          <Reveal delay={100} className="bg-ktc-ivoryLight rounded-xl border border-ktc-cream p-8">
            <Microscope className="text-ktc-gold" size={30} />
            <h3 className="font-serif text-2xl font-semibold text-ktc-green mt-4">Independent Inspection</h3>
            <p className="text-sm text-ktc-charcoal/70 mt-3 leading-relaxed">SGS and Bureau Veritas inspection is available on every shipment, giving buyers full confidence in quality, weight and specification compliance.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-20 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl font-semibold text-ktc-green">Need certificate copies for your order?</h2>
          <p className="text-ktc-charcoal/70 mt-4">Our team will share the relevant certifications and inspection details with your quote.</p>
          <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-8 py-3.5 rounded-md mt-8 transition-colors">Request a Quote <ArrowRight size={18} /></Link>
        </Reveal>
      </section>

      {/* Certificate viewer modal */}
      {viewer && (
        <div className="fixed inset-0 z-[60] bg-ktc-greenDark/95 flex items-center justify-center p-4 sm:p-6" onClick={() => setViewer(null)}>
          <button className="absolute top-6 right-6 text-ktc-ivory hover:text-ktc-goldLight" onClick={() => setViewer(null)}><X size={30} /></button>
          <div className="bg-ktc-ivoryLight rounded-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-6 py-4 border-b border-ktc-cream">
              <ShieldCheck size={22} className="text-ktc-gold" />
              <div>
                <div className="font-serif text-xl font-semibold text-ktc-green leading-none">{viewer.name}</div>
                <div className="text-xs text-ktc-charcoal/60 mt-1">{viewer.detail}</div>
              </div>
            </div>
            <div className="p-6">
              {viewer.image ? (
                <img src={resolveImg(viewer.image)} alt={viewer.name} className="w-full max-h-[70vh] object-contain rounded-lg border border-ktc-cream" />
              ) : (
                <div className="aspect-[3/4] max-h-[62vh] mx-auto w-full max-w-sm rounded-lg border-2 border-dashed border-ktc-gold/40 bg-ktc-cream/40 grid place-items-center text-center p-8">
                  <div>
                    <ImageOff className="mx-auto text-ktc-gold" size={40} />
                    <div className="font-serif text-2xl font-semibold text-ktc-green mt-4">{viewer.abbr} Certificate</div>
                    <p className="text-sm text-ktc-charcoal/65 mt-2">The scanned certificate document will appear here once uploaded.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certificates;
